import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface VersionInfo {
  commit: string;
  branch: string;
  buildTime: string;
}

interface BackendVersionInfo extends VersionInfo {}

export default function VersionPage() {
  const [backendVersion, setBackendVersion] = useState<BackendVersionInfo | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const frontendVersion: VersionInfo = __APP_VERSION__;

  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('version');
        if (error) {
          setBackendError(error.message);
        } else {
          setBackendVersion(data);
        }
      } catch (err) {
        setBackendError(err instanceof Error ? err.message : 'Failed to fetch backend version');
      } finally {
        setLoading(false);
      }
    };

    fetchBackendVersion();
  }, []);

  const formatDate = (isoString: string) => {
    if (isoString === 'unknown') return 'unknown';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Version Information
        </h1>

        {/* Frontend Version */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Frontend
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Git Commit</span>
              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                {frontendVersion.commit}
              </code>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Branch</span>
              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                {frontendVersion.branch}
              </code>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Build Time</span>
              <span className="text-gray-800 text-sm">
                {formatDate(frontendVersion.buildTime)}
              </span>
            </div>
          </div>
        </div>

        {/* Backend Version */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${
              loading ? 'bg-yellow-500 animate-pulse' : 
              backendError ? 'bg-red-500' : 'bg-green-500'
            }`}></span>
            Backend (Supabase Edge Functions)
          </h2>
          
          {loading ? (
            <div className="text-gray-500 text-center py-4">Loading...</div>
          ) : backendError ? (
            <div className="text-red-600 bg-red-50 p-4 rounded">
              <p className="font-medium">Failed to fetch backend version</p>
              <p className="text-sm mt-1">{backendError}</p>
            </div>
          ) : backendVersion ? (
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Git Commit</span>
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                  {backendVersion.commit}
                </code>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Branch</span>
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                  {backendVersion.branch}
                </code>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Build Time</span>
                <span className="text-gray-800 text-sm">
                  {formatDate(backendVersion.buildTime)}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Raw JSON */}
        <details className="mt-6">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
            View Raw JSON
          </summary>
          <pre className="mt-2 bg-gray-800 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
{JSON.stringify(
  {
    frontend: frontendVersion,
    backend: backendVersion || { error: backendError },
  },
  null,
  2
)}
          </pre>
        </details>
      </div>
    </div>
  );
}
